import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../Context';
import { useTranslation } from '../TranslationContext';
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

// Define the batch size
const BATCH_SIZE = 10;
// Time in milliseconds to wait before loading the next batch (optional, to space out requests)
const NEXT_BATCH_DELAY_MS = 500; 

function Gallery() {
    const { psServer, submittedCode, setShowModal, setModalImageIndex, galleryContainerRef, gallery, setStartDownloadAll, allowPurchase, setIsLoading, GDPRDaysLeft } = useAppContext();
    const { t } = useTranslation();

    const [showDownload, setShowDownload] = useState(false);
    const [loadedMediaCount, setLoadedMediaCount] = useState(0);
    const [processedGallery, setProcessedGallery] = useState([]);
    
    // New state to control which image URLs are exposed to the DOM (triggering retrieval)
    const [currentBatchEndIndex, setCurrentBatchEndIndex] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();

    // Redirection useEffect
    useEffect(() => {
        if (submittedCode === null || submittedCode === "") {
            navigate('/');
        }
    }, [submittedCode, navigate]);

    // Show Download Button useEffect
    useEffect(() => {
        if (gallery && gallery.length > 0) {
            const hasBoughtPhoto = gallery.some(photo => photo.bought);
            setShowDownload(hasBoughtPhoto);
        } else {
            setShowDownload(false);
        }
    }, [gallery]);
    
    // Initial Gallery Processing (Prepares all URLs)
    useEffect(() => {
        if (gallery && gallery.length > 0) {
            setIsLoading(true);
            setLoadedMediaCount(0);
            setCurrentBatchEndIndex(0); // Start with 0 URLs exposed

            const newProcessedGallery = gallery.map(photo => {
                const baseSrc = `${psServer}/media/thumb/${submittedCode}/${photo.fileName}/get`;
                const bustedSrc = `${baseSrc}${baseSrc.includes('?') ? '&' : '?'}${Date.now()}`;
                return { ...photo, bustedSrc: bustedSrc };
            });
            setProcessedGallery(newProcessedGallery);

            // Start the first batch immediately
            setCurrentBatchEndIndex(BATCH_SIZE); 
        } else if (gallery && gallery.length === 0) {
            setIsLoading(false);
            setProcessedGallery([]);
        }
    }, [gallery, psServer, submittedCode, setIsLoading]);

    // --- NEW LOGIC: Batch Retrieval Control and Loader Removal ---
    useEffect(() => {
        if (processedGallery.length === 0) return;

        // 1. Check if the current batch is fully loaded
        const isCurrentBatchLoaded = loadedMediaCount >= Math.min(currentBatchEndIndex, processedGallery.length);

        // 2. Load the next batch if the current one is done AND there are more photos to retrieve
        if (isCurrentBatchLoaded && currentBatchEndIndex < processedGallery.length) {
            
            const nextBatchStartIndex = currentBatchEndIndex;
            const nextBatchEndIndex = Math.min(nextBatchStartIndex + BATCH_SIZE, processedGallery.length);

            // Delay the request for the next batch
            const timer = setTimeout(() => {
                setCurrentBatchEndIndex(nextBatchEndIndex);
            }, NEXT_BATCH_DELAY_MS);

            return () => clearTimeout(timer); // Cleanup on unmount or re-run
        }

        // 3. Remove the Loader if ALL photos are loaded
        if (loadedMediaCount >= processedGallery.length) {
            // Your original 1-second delay for removing the loader
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    // Dependency cleanup: Only re-run when the count or the index changes.
    }, [loadedMediaCount, processedGallery.length, currentBatchEndIndex, setIsLoading]);
    // --- END NEW LOGIC ---

    function handleMediaClick(index) {
        setModalImageIndex(index);
        setShowModal(true);
    }

    function downloadAll(event) {
        setStartDownloadAll(true);
    }

    function helpPage() {
        window.open('https://www.imageinsight.com/support-cantfind/', '_blank', 'noopener noreferrer');
    }

    const handleClick = () => {
        navigate('/choices');
    };

    const containerVariants = {
        hidden: { opacity: 1 },
        visible: {
            opacity: 1,
        },
    };

    // This handler tracks every single image/video load
    const handleMediaLoad = () => {
        setLoadedMediaCount(prevCount => prevCount + 1);
    };

    return (
        <>
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="galleryContainer" ref={galleryContainerRef}
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="buttons"
                >
                    {allowPurchase && (
                        <button onClick={handleClick}>{t('buttonGetMore')}</button>
                    )}
                    {showDownload && (
                        <button onClick={downloadAll}>{t('buttonDownload')}</button>
                    )}
                    <button onClick={helpPage}>{t('buttonHelp')}</button>
                </motion.div>
                <div className="countdownNotice">{t('countDownNotice', {v: GDPRDaysLeft})}</div>
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
                    <AnimatePresence>
                        <ResponsiveMasonry columnsCountBreakPoints={{ 740: 2, 1110: 3, 1490: 4, 1910: 5, 2460: 6 }} columnClassName="test">
                            <Masonry gutter="10px">
                                {/* RENDER ALL PHOTOS, BUT ONLY ASSIGN SRC TO THE CURRENT BATCH */}
                                {processedGallery.map((photo, index) => {
                                    // Check if the current photo is in the batch that should be retrieving its source
                                    const shouldRetrieve = index < currentBatchEndIndex; 
                                    // Conditionally set the src to trigger retrieval only if it's in the current batch
                                    const srcToUse = shouldRetrieve ? photo.bustedSrc : '';

                                    return (
                                        <React.Fragment key={photo.id}>
                                            {photo.fileName.endsWith('.mp4') ? (
                                                <motion.video
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    whileInView={{ scale: 1, opacity: 1 }}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                                    viewport={{ once: true }}
                                                    muted={true}
                                                    autoPlay={true}
                                                    playsInline={true}
                                                    loop
                                                    preload="auto"
                                                    className={`photo ${photo.bought ? `download` : ''}`}
                                                    id={`photo_${photo.id}`}
                                                    onClick={photo.bought ? () => handleMediaClick(index) : () => handleClick()}
                                                    style={{
                                                        filter: photo.bought ? 'none' : 'grayscale(100%)'
                                                    }}
                                                    // The load event handler should only fire when src is present/valid
                                                    onLoadedData={shouldRetrieve ? handleMediaLoad : undefined} 
                                                >
                                                    {/* Conditionally provide the source URL */}
                                                    {shouldRetrieve && (
                                                        <source
                                                            src={srcToUse}
                                                            type="video/mp4"
                                                        />
                                                    )}
                                                </motion.video>
                                            ) : (
                                                <motion.img
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    whileInView={{ scale: 1, opacity: 1 }}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                                    viewport={{ once: true }}
                                                    src={srcToUse} // Conditionally set the src
                                                    alt=""
                                                    className={`photo ${photo.bought ? `download` : ''}`}
                                                    id={`photo_${photo.id}`}
                                                    onClick={photo.bought ? () => handleMediaClick(index) : () => handleClick()}
                                                    // The load event handler should only fire when src is present/valid
                                                    onLoad={shouldRetrieve ? handleMediaLoad : undefined}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </Masonry>
                        </ResponsiveMasonry>
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        </>
    );
}

export default Gallery;